import 'src/utils/polyfill';
import { FC, PropsWithChildren, ReactNode, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@emotion/react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ErrorBoundary } from '@sentry/nextjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as JotaiProvider, useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/react/utils';
import { queryClientAtom } from 'jotai-tanstack-query';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import { debounceTime, fromEvent } from 'rxjs';
import appConfig from 'app-config';
import ErrorComponent from 'src/components/Error';
import FirstOpenManager from 'src/components/FirstOpenManager';
import GoogleThirdPartyScripts from 'src/components/GoogleThirdPartyScripts';
import HeartbeatManager from 'src/components/HeartbeatManager';
import Layout from 'src/components/Layout';
import GoogleOAuthRedirectHandler from 'src/components/LoginGoogle/GoogleOAuthRedirectHandler';
import MetaRefresh from 'src/components/MetaRefresh';
import SeoKeywords from 'src/components/SeoKeywords';
import UTMManager from 'src/components/UTMManager';
import { DEBUG } from 'src/config';
import useIsRtl from 'src/hooks/useIsRtl';
import I18nProvider from 'src/i18n/I18nProvider';
import store from 'src/stores';
import { nextPagePropsAtom } from 'src/stores/pageProps/atoms';
import { PageProps } from 'src/stores/pageProps/types';
import { notoSans, poppins, firaCode } from 'src/styles/fonts';
import GlobalStyles from 'src/styles/GlobalStyles';
import baseTheme from 'src/styles/theme';
import { googleAppId } from 'src/types/OAuthToken';
import getCanonicalUrl from 'src/utils/canonicalUrl';
import getDeviceInfo from 'src/utils/device/info';
import { setViewportUnits } from 'src/utils/layout';
import { destroyPerfMonitor, initPerfMonitor } from 'src/utils/perf';
import { destroyPerfStore, initPerfStore } from 'src/utils/perf/store';
import 'normalize.css/normalize.css';
import { isSystemDarkMode } from 'src/styles/isDark';
import { createSystemColor } from 'src/styles/color';
import { safeWindow } from 'src/utils/static-generation';
import { initWebVitalsReporting } from 'src/utils/webVitals';
import AppsflyerPBAThirdPartyScript from 'src/components/AppsflyerPBAThirdPartyScript';
import { isDarkModeAtom } from 'src/stores/styles/atom';
import useIsomorphicLayoutEffect from 'src/hooks/useIsomorphicLayoutEffect';
import SafariToolbarTint from 'src/components/SafariToolbarTint';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false // axiosRetry가 이미 처리하므로 TanStack Query 재시도 비활성화
    }
  }
});
const HydrateAtoms = ({
  children
}: {
  children: ReactNode;
}) => {
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return <>{children}</>;
};
const JotaiDevTools = dynamic(() => import('src/components/JotaiDevTools').catch(() => Promise.resolve(() => <></>)), {
  ssr: false
});
const ReactQueryDevtools = dynamic(async () => {
  try {
    return (await import('@tanstack/react-query-devtools')).ReactQueryDevtools;
  } catch {
    return Promise.resolve(() => <></>);
  }
}, {
  ssr: false
});
const Debug = dynamic(() => import('src/components/Debug').catch(() => Promise.resolve(() => <></>)), {
  ssr: false
});
interface Props extends AppProps {
  pageProps: PageProps;
}
const ThemeProviderWithRtl: FC<PropsWithChildren> = ({
  children
}) => {
  const isRtl = useIsRtl();
  const isDark = useAtomValue(isDarkModeAtom);
  const color = useMemo(() => {
    return createSystemColor(isDark);
  }, [isDark]);
  const theme = useMemo(() => {
    return {
      ...baseTheme,
      color,
      isRtl,
      isDark
    };
  }, [isRtl, isDark, color]);
  return <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-component="ThemeProviderWithRtl" data-sentry-source-file="_app.tsx">{children}</ThemeProvider>;
};
function MyApp({
  Component,
  pageProps
}: Props) {
  const {
    metas = [{
      property: 'og:title',
      content: 'OG_TITLE',
      key: 'og:title'
    }, {
      name: 'description',
      content: 'OG_DESC',
      key: 'description'
    }, {
      property: 'og:description',
      content: 'OG_DESC',
      key: 'og:description'
    }, {
      property: 'og:image',
      content: `${appConfig.serviceCode}/images/og/openGraph.png`,
      key: 'og:image'
    }]
  } = pageProps;
  const router = useRouter();

  // 현재 페이지의 경로
  const pathname = router.pathname;

  // URL의 쿼리 파라미터
  const query = router.query;
  const [userInteracted, setUserInteracted] = useState(false);
  const {
    isMobile,
    isWebKit
  } = getDeviceInfo();
  const isAndroid = isMobile && !isWebKit;
  useEffect(() => {
    initPerfMonitor();
    initPerfStore(store);
    initWebVitalsReporting();
    const onInitialInteraction = () => {
      setUserInteracted(true);
      window.removeEventListener('pointerdown', onInitialInteraction);
    };
    window.addEventListener('pointerdown', onInitialInteraction);
    setViewportUnits();
    const subscription = fromEvent(window.visualViewport ?? window, 'resize').pipe(debounceTime(100)).subscribe(() => {
      setViewportUnits();
    });
    return () => {
      subscription.unsubscribe();
      destroyPerfStore();
      destroyPerfMonitor();
    };
  }, []);
  useEffect(() => {
    document.body.classList.add('init');
    if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'Prod') {
      if (process.env.NEXT_PUBLIC_RELEASE) {
        // eslint-disable-next-line no-console
        console.log(`Build version: ${process.env.NEXT_PUBLIC_RELEASE}`);
      }
    }
  }, []);

  // pageProps를 store에 저장한 이후에 UI를 렌더링해야 하므로 useMemo를 사용합니다.
  useMemo(() => {
    store.set(nextPagePropsAtom, pageProps);
  }, [pageProps]);
  useIsomorphicLayoutEffect(() => {
    const isDarkMode = pageProps?.supportsDarkAndLightMode || safeWindow?.AzarJs ? isSystemDarkMode() : true;
    if (isDarkMode !== undefined) {
      store.set(isDarkModeAtom, isDarkMode);
    }
  }, [pageProps?.supportsDarkAndLightMode]);

  /**
   * https://stackoverflow.com/questions/76026292/why-is-window-innerheight-incorrect-until-i-tap-chrome-android
   * 안드로이드 Chrome 환경에서 window.innerHeight가 정상값보다 크게 측정되고 최초 터치 이후 정상화되는 이슈
   *
   * interactive-widget 키 지원: Blink 엔진 기반 Chrome 108 버전 이상 (iOS는 Webkit 기반 동작이기 때문에 영향 없음)
   * https://developer.chrome.com/blog/viewport-resize-behavior?hl=ko
   */
  const metaViewportInteractive = useMemo(() => isAndroid && !userInteracted ? ', interactive-widget=resizes-content' : '', [isAndroid, userInteracted]);
  if (pageProps.metaRefreshInfo) {
    return <MetaRefresh metaRefreshInfo={pageProps.metaRefreshInfo} />;
  }
  return <>
      {/* _app에서 font 객체를 사용해야 next/font CSS 청크(@font-face)가 초기 번들에 포함됨
          (_document만으로는 CSS 청크 로딩이 트리거되지 않음, vercel/next.js#44840) */}
      <style dangerouslySetInnerHTML={{
      __html: `
            :root {
              --font-poppins: ${poppins.style.fontFamily};
              --font-noto-sans: ${notoSans.style.fontFamily};
              --font-fira-code: ${firaCode.style.fontFamily};
            }`
    }} />
      <ErrorBoundary fallback={() => <ErrorComponent />} data-sentry-element="ErrorBoundary" data-sentry-source-file="_app.tsx">
        <GoogleThirdPartyScripts data-sentry-element="GoogleThirdPartyScripts" data-sentry-source-file="_app.tsx" />
        <AppsflyerPBAThirdPartyScript data-sentry-element="AppsflyerPBAThirdPartyScript" data-sentry-source-file="_app.tsx" />
        <Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
          <meta name='facebook-domain-verification' content='obrg4zuhup370msixm63loi0g8abga' data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
          {/* PWA 모드에서 env(safe-area-inset-*) CSS 변수 사용을 위해 viewport-fit=cover 추가 */}
          <meta name='viewport' content={`width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover${metaViewportInteractive}`} data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
          {pageProps.noindex && <meta key={pathname} name='robots' content='noindex, nofollow' />}
          {pageProps.hreflangs?.map(({
          hreflang,
          href
        }) => <link key={href + hreflang} rel='alternate' hrefLang={hreflang} href={href} />)}
          {pageProps.canonical && <link key={pathname} rel='canonical' href={getCanonicalUrl(pathname, query)} />}
        </Head>
        <GoogleOAuthProvider clientId={googleAppId} data-sentry-element="GoogleOAuthProvider" data-sentry-source-file="_app.tsx">
          <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
            <JotaiProvider store={store} data-sentry-element="JotaiProvider" data-sentry-source-file="_app.tsx">
              {/* HydrateAtoms queryClient를 inject해주는 역할 */}
              <HydrateAtoms data-sentry-element="HydrateAtoms" data-sentry-source-file="_app.tsx">
                {/* 번역리소스가 로드되기 전에 더미로 SEO Keyword를 추가합니다.
                  번역리소스 로드 이후에 실제 번역이 반영됩니다. (Layout 내부에서) */}
                <SeoKeywords metas={metas} data-sentry-element="SeoKeywords" data-sentry-source-file="_app.tsx" />
                <I18nProvider data-sentry-element="I18nProvider" data-sentry-source-file="_app.tsx">
                  <ThemeProviderWithRtl data-sentry-element="ThemeProviderWithRtl" data-sentry-source-file="_app.tsx">
                    <GlobalStyles data-sentry-element="GlobalStyles" data-sentry-source-file="_app.tsx" />
                    <SafariToolbarTint data-sentry-element="SafariToolbarTint" data-sentry-source-file="_app.tsx" />
                    <Layout metas={metas} data-sentry-element="Layout" data-sentry-source-file="_app.tsx">
                      <GoogleOAuthRedirectHandler data-sentry-element="GoogleOAuthRedirectHandler" data-sentry-source-file="_app.tsx" />
                      <UTMManager data-sentry-element="UTMManager" data-sentry-source-file="_app.tsx" />
                      <FirstOpenManager data-sentry-element="FirstOpenManager" data-sentry-source-file="_app.tsx" />
                      <HeartbeatManager data-sentry-element="HeartbeatManager" data-sentry-source-file="_app.tsx" />
                      <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
                      {DEBUG && <>
                          <JotaiDevTools store={store} />
                          <ReactQueryDevtools />
                          <Debug />
                        </>}
                    </Layout>
                  </ThemeProviderWithRtl>
                </I18nProvider>
              </HydrateAtoms>
            </JotaiProvider>
          </QueryClientProvider>
        </GoogleOAuthProvider>
      </ErrorBoundary>
    </>;
}
export default appWithTranslation(MyApp);